import React, { useState } from 'react';
import { Card, Form, Select, DatePicker, TimePicker, Button, Space, Typography, message, Divider, Grid, Row, Col } from 'antd';
import { SyncOutlined, SaveOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { zkTimeService } from '../services/zkTimeService';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const TimeManagementTab: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState<string | null>(null);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const machines = [
    { ip: '192.168.1.163', name: 'Máy chấm công 163' },
    { ip: '192.168.1.164', name: 'Máy chấm công 164' },
  ];
  
  // ... (các hàm xử lý giữ nguyên)
  const handleGetCurrentTime = async () => {
    const machineIp = form.getFieldValue('machineIp');
    if (!machineIp) {
      message.warning('Vui lòng chọn máy chấm công');
      return;
    }

    setLoading(true);
    try {
      const response = await zkTimeService.getCurrentTime(machineIp);
      const timeStr = response.data.current_time;
      setCurrentTime(timeStr);
      
      // Đồng bộ vào form để người dùng dễ điều chỉnh
      const m = dayjs(timeStr);
      if (m.isValid()) {
        form.setFieldsValue({
          newDate: m,
          newTimeOnly: m
        });
      }
      
      message.success('Đã lấy thời gian từ máy chấm công');
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Lỗi khi lấy thời gian';
      const details = error.response?.data?.details ? ` (${error.response.data.details})` : '';
      message.error(`${errorMsg}${details}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTime = async (values: any) => {
    setLoading(true);
    try {
      const { machineIp, newDate, newTimeOnly } = values;
      
      // Tạo đối tượng DateTime kết hợp ngày và giờ, sau đó format theo chuẩn ISO
      const combined = dayjs(newDate)
        .hour(newTimeOnly.hour())
        .minute(newTimeOnly.minute())
        .second(newTimeOnly.second());
      
      const formattedTime = combined.format('YYYY-MM-DDTHH:mm:ss');
      
      const response = await zkTimeService.updateTime(machineIp, formattedTime);
      setCurrentTime(response.data.current_time);
      message.success('Cập nhật thời gian thành công');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Lỗi khi cập nhật thời gian');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTime = async () => {
    const machineIp = form.getFieldValue('machineIp');
    if (!machineIp) {
      message.warning('Vui lòng chọn máy chấm công');
      return;
    }

    setLoading(true);
    try {
      const response = await zkTimeService.syncTime(machineIp);
      const timeStr = response.data.current_time;
      setCurrentTime(timeStr);
      
      const m = dayjs(timeStr);
      if (m.isValid()) {
        form.setFieldsValue({
          newDate: m,
          newTimeOnly: m
        });
      }
      
      message.success('Đồng bộ thời gian với máy chủ thành công');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Lỗi khi đồng bộ thời gian');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: isMobile ? '8px' : '0px' }}>
      <Card 
        title={<Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>Quản lý thời gian máy chấm công</Title>} 
        bordered={false}
        bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateTime}
          initialValues={{ 
            machineIp: machines[0].ip,
            newDate: dayjs(),
            newTimeOnly: dayjs()
          }}
        >
          <Form.Item
            name="machineIp"
            label={<Text strong>Chọn máy chấm công</Text>}
            rules={[{ required: true, message: 'Vui lòng chọn máy' }]}
          >
            <Select placeholder="-- Chọn máy --" size={isMobile ? 'large' : 'middle'}>
              {machines.map((m) => (
                <Option key={m.ip} value={m.ip}>
                  {m.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ 
            marginBottom: '24px', 
            padding: isMobile ? '12px' : '16px', 
            background: '#f5f5f5', 
            borderRadius: '8px',
            border: '1px solid #e8e8e8'
          }}>
            <Space direction="vertical" style={{ width: '100%' }} size={isMobile ? 'small' : 'middle'}>
              <Text type="secondary">Thời gian trên máy hiện tại:</Text>
              <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: currentTime ? '#1890ff' : '#bfbfbf' }}>
                {currentTime || 'Chưa lấy dữ liệu'}
              </Title>
              <Button 
                icon={<ClockCircleOutlined />} 
                onClick={handleGetCurrentTime} 
                loading={loading}
                block={isMobile}
                type="dashed"
              >
                Lấy thời gian hiện tại
              </Button>
            </Space>
          </div>

          <Divider orientation="left" style={{ margin: '24px 0 16px' }}>
            <Text type="secondary">Điều chỉnh thời gian</Text>
          </Divider>

          <Form.Item label={<Text strong>Thời gian mới</Text>} required>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={14}>
                <Form.Item
                  name="newDate"
                  noStyle
                  rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    size={isMobile ? 'large' : 'middle'}
                    placeholder="Chọn ngày"
                    format="DD/MM/YYYY"
                    inputReadOnly={isMobile}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10}>
                <Form.Item
                  name="newTimeOnly"
                  noStyle
                  rules={[{ required: true, message: 'Vui lòng chọn giờ' }]}
                >
                  <TimePicker 
                    style={{ width: '100%' }} 
                    size={isMobile ? 'large' : 'middle'}
                    placeholder="Chọn giờ"
                    format="HH:mm:ss"
                    inputReadOnly={isMobile}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space 
              direction={isMobile ? 'vertical' : 'horizontal'} 
              style={{ width: '100%' }} 
              size="middle"
            >
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                htmlType="submit" 
                loading={loading}
                block={isMobile}
                size={isMobile ? 'large' : 'middle'}
              >
                Cập nhật thủ công
              </Button>
              <Button 
                icon={<SyncOutlined />} 
                onClick={handleSyncTime} 
                loading={loading}
                block={isMobile}
                size={isMobile ? 'large' : 'middle'}
              >
                Đồng bộ với máy chủ
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default TimeManagementTab;
